import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { AppDispatch } from "../store";
import { smsTemplateActions } from "../slices/smsTemplate";
import { getErrorMessage } from "../../utils/errorUtils";

import { SmsTemplateRequestDto } from "../../shared/dtos/sms/SmsTemplateRequestDto";
import { SmsTemplateResponseDto } from "../../shared/dtos/sms/SmsTemplateResponseDto";
import { UpdateSmsTemplatePayload } from "../../shared/interfaces/sms/UpdateSmsTemplatePayload";
import { ApiResponseDto } from "../../shared/dtos/common/ApiResponseDto";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";

export const createSmsTemplate =
  (values: SmsTemplateRequestDto) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(smsTemplateActions.setLoadingCreate());
    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<SmsTemplateResponseDto>
      >("/sms/templates", values, {
        headers: { requiresAuth: true },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Template created successfully!");
        dispatch(smsTemplateActions.createSuccess(data.data));
        dispatch(
          smsTemplateActions.setServerStatus({
            operation: "create",
            status: data.statusCode || 201,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(smsTemplateActions.setErrorCreate(message));
    }
  };

export const getSmsTemplateById =
  (id: string) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(smsTemplateActions.setLoadingFetchOne());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<SmsTemplateResponseDto>
      >(`/sms/templates/${id}`, {
        headers: { requiresAuth: true },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(smsTemplateActions.setItem(data.data));
      }
    } catch (error) {
      dispatch(smsTemplateActions.setErrorFetchOne(getErrorMessage(error)));
    }
  };

export const getSmsTemplates =
  () =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(smsTemplateActions.setLoadingFetchAll());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<SmsTemplateResponseDto[]>
      >("/sms/templates", {
        headers: { requiresAuth: true },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(smsTemplateActions.setItems(data.data));
      }
    } catch (error) {
      dispatch(smsTemplateActions.setErrorFetchAll(getErrorMessage(error)));
    }
  };

export const updateSmsTemplate =
  (payload: UpdateSmsTemplatePayload) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(smsTemplateActions.setLoadingUpdate());
    try {
      const { data } = await axiosInstance.put<
        ApiResponseDto<SmsTemplateResponseDto>
      >(`/sms/templates/${payload.id}`, payload.values, {
        headers: { requiresAuth: true },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Template updated successfully!");
        dispatch(smsTemplateActions.updateSuccess(data.data));
        dispatch(
          smsTemplateActions.setServerStatus({
            operation: "update",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(smsTemplateActions.setErrorUpdate(message));
    }
  };

export const deleteSmsTemplate =
  (id: string) =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(smsTemplateActions.setLoadingDelete());
    try {
      const { data } = await axiosInstance.delete<ApiResponseDto<void>>(
        `/sms/templates/${id}`,
        {
          headers: { requiresAuth: true },
        },
      );
      if (data.status === ApiResponseStatus.SUCCESS) {
        toast.success(data.message || "Template deleted successfully!");
        dispatch(smsTemplateActions.deleteSuccess(id));
        dispatch(
          smsTemplateActions.setServerStatus({
            operation: "delete",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(smsTemplateActions.setErrorDelete(message));
    }
  };

export const resetSmsTemplateServerStatus =
  () =>
  (dispatch: AppDispatch): void => {
    dispatch(smsTemplateActions.setServerStatus(null));
  };
